import { atom, useAtom } from "jotai";
import { atomWithStorage } from "jotai/utils";
import {
  Doctor,
  Location,
  MedicalAssistant,
  OnsiteOpsConfig,
  User,
} from "~/api/API";
import { getCurrentEnv } from "~/common/utils";
import { formatDoctorDisplayName } from "~/utils/doctorName";

export const isProdAtom = atomWithStorage<boolean>(
  "isProd",
  getCurrentEnv() === "prod",
);

export const allAssistantsAtom = atom<MedicalAssistant[]>([]);
export const allDoctorsAtom = atom<Doctor[]>([]);
export const allLocationsAtom = atom<Location[]>([]);

/**
 * False until the reference lists above have settled (populated or failed).
 * They start empty, so a field holding an entity ID has nothing to resolve the
 * ID against until then — consumers use this to render a loading state instead
 * of the bare ID. Set by useInitializeData.
 */
export const referenceDataLoadedAtom = atom(false);

export interface AppUser {
  user: User;
  displayName: string;
}

/** Derived atom: all users from assistants and doctors, keyed by User.id */
export const allUsersAtom = atom<Map<string, AppUser>>((get) => {
  const assistants = get(allAssistantsAtom);
  const doctors = get(allDoctorsAtom);
  const map = new Map<string, AppUser>();

  for (const ma of assistants) {
    if (ma.user?.id) {
      map.set(ma.user.id, { user: ma.user, displayName: ma.name ?? "" });
    }
  }
  for (const doc of doctors) {
    if (doc.user?.id) {
      map.set(doc.user.id, {
        user: doc.user,
        displayName: doc.name ?? formatDoctorDisplayName(doc),
      });
    }
  }

  return map;
});
export const allOnsiteOpsConfigsAtom = atom<OnsiteOpsConfig[]>([]);

export const uploadedIntakeFormS3KeysAtom = atom<string[]>([]);

export const useIsProdEnv = () => {
  const [isProd] = useAtom(isProdAtom);
  return isProd;
};
